import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { isNil } from 'lodash';
import { Subscription } from 'rxjs';
import { GlobalConstants } from 'src/app/constant/constant';
import { Activity } from 'src/app/entities/Activity';
import { TimeRecord } from 'src/app/entities/timeRecord';
import { FormControlParams } from 'src/app/models/forms/form-control-params';
import { NotableActivityOptionService } from 'src/app/services/notable-activity-option.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { ProjectService } from 'src/app/services/project.service';
import { TimeService } from 'src/app/services/time.service';
import { TimeRecordService } from 'src/app/services/timeRecord.service';
import { IonSelectSearchableComponent } from 'src/app/shared/common/ionSelectSearchable/ionSelectSearchable.component';
import { DateTimeHelper } from 'src/app/utils/date-time-helper';
import { NotableActivityOption } from './../../../entities/NotableActivityOption';
import { ActivityService } from 'src/app/services/activity.service';
import { NotableActivityFormControls } from 'src/app/models/form-groups/notable-activity-form.model';
import { ActivityType } from 'src/app/constant/activity-constants';

export interface NotableActivityFormControlsValues {
  userId: number;
  dateTime: string;
  timeZoneId: string;
  text: string;
  otherText: string;
}

export interface NotableActivityFormDataValues extends Omit<NotableActivityFormControlsValues, 'otherText'> {
  timeRecordRecordId: number;
  isValid: boolean;
}

export interface NotableActivityFormControlParams {
  dateTime: FormControlParams<string>;
  notableActivityToEdit?: Activity;
  skipEnclosingTimeRecordCheck?: boolean;
}

@Component({
  selector: 'app-notable-activity',
  templateUrl: './notable-activity.component.html',
  styleUrls: ['./notable-activity.component.scss'],
})
export class NotableActivityComponent implements OnInit, OnDestroy {
  @Input() userId: number;
  @Input() interval: number;
  @Input() notableActivityFormControlParams: NotableActivityFormControlParams;
  @Input() timeZoneId: string;
  @Output() formValuesChanged = new EventEmitter<NotableActivityFormDataValues>();

  formGroup: FormGroup<NotableActivityFormControls>;
  enclosingTimeRecord: TimeRecord;
  laborCategoryId: number;
  notableActivities: NotableActivityOption[] = [];
  entityTypeId: number;
  minInterval = 15;

  isPageReady = false;

  private formGroupValueSubscription: Subscription;
  private formGroupStatusSubscription: Subscription;

  constructor(
    private projectService: ProjectService,
    private timeService: TimeService,
    private notableActivityOptionService: NotableActivityOptionService,
    private timeRecordService: TimeRecordService,
    private modalController: ModalController,
    private notificationsService: NotificationsService,
    private activityService: ActivityService
  ) {

   }

  async ngOnInit() {
    this.formGroup = new FormGroup<NotableActivityFormControls>({
      userId: new FormControl(this.userId, [Validators.required]),
      dateTime: new FormControl(this.notableActivityFormControlParams.dateTime.initialValue, [Validators.required]),
      timeZoneId: new FormControl(this.timeZoneId, [Validators.required]),
      text: new FormControl(this.notableActivityFormControlParams.notableActivityToEdit ? this.notableActivityFormControlParams.notableActivityToEdit.Text : '', [Validators.required]),
      otherText: new FormControl(this.notableActivityFormControlParams.notableActivityToEdit ? this.notableActivityFormControlParams.notableActivityToEdit.Text : '', [
        Validators.required,
      ]),
    });

    if (!this.notableActivityFormControlParams.skipEnclosingTimeRecordCheck) {
      await this.loadEnclosingTimeRecord();
    }
    await this.validateSelectedDateTime();
    this.updateActivityTextValidators();
    this.validateSelectedNotableActivity();

    this.formGroupStatusSubscription = this.formGroup.statusChanges.subscribe(() => this.emitFormChanged());

    this.formGroupValueSubscription = this.formGroup.valueChanges.subscribe(async change => {
      await this.validateSelectedDateTime();
      if (!this.notableActivityFormControlParams.skipEnclosingTimeRecordCheck) {
        await this.loadEnclosingTimeRecord();
      }
      this.emitFormChanged();
    });

    this.emitFormChanged();

    await this.displayVerifiedTimeWarningIfNeeded();

    this.isPageReady = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.formGroup && changes.NotableActivityFormControlsFormControlParams) {
      this.validateSelectedDateTime(false);
    }
  }

  ngOnDestroy() {
    this.formGroupStatusSubscription.unsubscribe();
    this.formGroupValueSubscription.unsubscribe();
  }

  emitFormChanged() {
    this.formValuesChanged.emit({
      userId: this.formGroup.value.userId,
      dateTime: this.formGroup.value.dateTime,
      timeZoneId: this.formGroup.value.timeZoneId,
      text: this.getNotableActivityText(),
      timeRecordRecordId: this.enclosingTimeRecord?.RecordId,
      isValid: this.formGroup.valid && (this.notableActivityFormControlParams.skipEnclosingTimeRecordCheck || !isNil(this.enclosingTimeRecord)),
    });
  }

  private updateActivityTextValidators() {
    const notableActivityControl = this.formGroup.controls.text;
    const otherNotableActivityControl = this.formGroup.controls.otherText;

    if (this.notableActivities.length > 0) {
      notableActivityControl.setValidators([Validators.required]);
    } else {
      notableActivityControl.clearValidators();
      notableActivityControl.reset('', { emitEvent: false });
    }

    if (!this.notableActivities || this.notableActivities.length === 0 || notableActivityControl.value.toLocaleLowerCase() === 'other') {
      otherNotableActivityControl.setValidators([Validators.required, Validators.minLength(2)]);
    } else {
      otherNotableActivityControl.clearValidators();
      otherNotableActivityControl.reset('', { emitEvent: false });
    }

    notableActivityControl.updateValueAndValidity({ emitEvent: false });
    otherNotableActivityControl.updateValueAndValidity({ emitEvent: false });
  }

  private async displayVerifiedTimeWarningIfNeeded() {
      const isDateHidden = this.notableActivityFormControlParams.dateTime?.isHidden ?? false;
      const isVerified = await this.timeService.checkIsTimeVerifiedForDate(this.userId, this.formGroup.controls.dateTime.value)
      if (isVerified && !isDateHidden) {
        this.notificationsService.showAlert({
          header: 'Warning',
          message: 'The date you are modifying has already been verified. Your supervisor will need to return your activity log to you before edits can be made.',
          buttons: [{ text: 'OK' }],
          suppression: {
            suppressKey: GlobalConstants.SUPPRESS_ALREADY_VERIFIED_ALERT_DATE,
            suppressDuration: 8,
            suppressLabel: "Hide this message",
          }
        });
      }
  }

  private async validateSelectedDateTime(emitEvent = true) {
    const dateTimeControl = this.formGroup.controls.dateTime;
    const minDateTime = this.notableActivityFormControlParams.dateTime.minValue;
    const maxDateTime = this.notableActivityFormControlParams.dateTime.maxValue;

    if (dateTimeControl.value) {
      if (minDateTime) {
        if (DateTimeHelper.checkTimeIsBefore(dateTimeControl.value, this.timeZoneId, minDateTime, this.timeZoneId)) {
          dateTimeControl.setErrors({ startDateTimeBeforeMin: true }, { emitEvent });
        }
      }

      if (maxDateTime) {
        if (DateTimeHelper.checkTimeIsAfter(dateTimeControl.value, this.timeZoneId, maxDateTime, this.timeZoneId)) {
          dateTimeControl.setErrors({ startDateTimeAfterMax: true }, { emitEvent });
        }
      }

      const newActivity = new Activity ({
        UserId: this.userId,
        UserSpecifiedDate: this.formGroup.value.dateTime,
        TimeZoneId: this.formGroup.value.timeZoneId,
        Text: this.formGroup.value.text,
        Type: ActivityType.NotableActivity
      });

      const duplicate = await this.activityService.getDuplicateActivity(newActivity);
      if (duplicate) {
        dateTimeControl.setErrors({ duplicate: true }, { emitEvent });
      }
    }
  }

  private async loadEnclosingTimeRecord() {
    this.enclosingTimeRecord = await this.timeRecordService.getWorkingTimeRecordEnclosingDatetime(this.userId, this.formGroup.value.dateTime, this.formGroup.value.timeZoneId);
    this.loadNotableActivities(this.enclosingTimeRecord?.LaborCategoryId);

    await this.loadMinInterval();
  }

  private async loadNotableActivities(laborCategoryId: number | null) {
    if (!laborCategoryId) {
      this.notableActivities = [];
    } else {
      this.notableActivities = await this.notableActivityOptionService.getNotableActivityOptionsByLaborCategoryId(laborCategoryId);
    }
    this.updateActivityTextValidators();
    this.validateSelectedNotableActivity();
  }

  private validateSelectedNotableActivity(resetText = false) {
    const notableActivityValue = this.formGroup.controls.text.value;

    if (notableActivityValue.length > 0) {
      if (this.notableActivities && this.notableActivities.length > 0) {
        const selectedNotableActivity = this.notableActivities.find(activity => activity.Name.toLocaleLowerCase() === notableActivityValue.toLocaleLowerCase());
        if (!selectedNotableActivity) {
          if (this.notableActivities.find(activity => activity.Name.toLocaleLowerCase() === 'other')) {
            this.formGroup.patchValue({ text: 'Other', otherText: notableActivityValue  }, { emitEvent: false });
          } else {
            this.formGroup.patchValue({ text: '', otherText: '' }, { emitEvent: false });
          }
        }
      }
    } else if (resetText) {
      this.formGroup.patchValue({ text: '', otherText: '' }, { emitEvent: false });
    }
  }

  private async loadMinInterval() {
    if (this.interval === 1) {
      this.minInterval = 1;
      return;
    }

    if (this.enclosingTimeRecord) {
      const project = await this.projectService.getProjectByOnlineId(this.enclosingTimeRecord.ProjectId);
      if (project) {
        this.minInterval = project.TimeRoundTo;
      } else {
        this.minInterval = this.interval;
      }
    } else {
      this.minInterval = this.interval;
    }
  }

  private getNotableActivityText(): string {
    if (!this.formGroup.value.text || this.formGroup.value.text.toLocaleLowerCase() === 'other') {
      return this.formGroup.value.otherText;
    } else {
      return this.formGroup.value.text;
    }
  }

  async onTimeSelect(dateTime: string) {
    this.formGroup.patchValue({ dateTime });
    await this.displayVerifiedTimeWarningIfNeeded();
  }

  async onLaborCategoryChange(laborCategoryId: number | null) {
    await this.loadNotableActivities(laborCategoryId);
    this.validateSelectedNotableActivity(true);
  }

  getValidationErrors(control: AbstractControl): string[] {
    if (!control.errors) {
      return [];
    }

    return Object.keys(control.errors).map(key => {
      // Don't show required error if control is not touched
      if (!control.touched && key === 'required' ) {
        return '';
      }

      switch (key) {
        case 'required':
          return 'This field is required';
        case 'minlength':
          return 'This field must be at least 2 characters';
        case 'startDateTimeBeforeMin':
          return `Start time is before the earliest allowed time ${DateTimeHelper.formatDateTime(this.notableActivityFormControlParams.dateTime.minValue, 'HH:mm')}`;
        case 'startDateTimeAfterMax':
          return `Start time is after the latest allowed time ${DateTimeHelper.formatDateTime(this.notableActivityFormControlParams.dateTime.maxValue, 'HH:mm')}`;
        case 'duplicate':
          return 'This activity is already recorded for this time';
        default:
          return 'Unknown error';
      }
    });
  }

  async openNotableActivitiesModal() {
    const modal = await this.modalController.create({
      component: IonSelectSearchableComponent,
      componentProps: {
        value: this.notableActivities,
        displayProperty: 'Name',
        showSearchBoxCountGreaterThan: GlobalConstants.SEARCH_LIST_ITEM_DISPLAY_LIMIT,
      },
      cssClass: "dropdown-options-modal"
    });

    modal.onDidDismiss().then(dataReturned => {
      if (dataReturned.data) {
        const notableActivity: NotableActivityOption = dataReturned.data;
        this.formGroup.patchValue({ text: notableActivity.Name });
        this.updateActivityTextValidators();
      }
      modal.remove();
    });

    await modal.present();
  }

  markAsTouched() {
    if (this.formGroup) {
      Object.values(this.formGroup.controls).forEach(control => {
        control.markAsTouched();
        control.updateValueAndValidity();
      });
    }
  }
}
